import React, { useEffect, useState, useRef } from 'react';
import { Filter, SlidersHorizontal, ChevronDown, X, Check } from 'lucide-react';
export type SortKey = 'urgency' | 'stale' | 'amount' | 'recent' | 'stage';
export type ViewKey = 'all' | 'critical' | 'dial' | 'mine' | 'highvalue';
export interface AdvancedFilters {
  stages: string[];
  states: string[];
  minAmount: number | null;
  maxAmount: number | null;
  minHoursStale: number | null;
  assignedLOs: string[];
}
export const emptyAdvancedFilters: AdvancedFilters = {
  stages: [],
  states: [],
  minAmount: null,
  maxAmount: null,
  minHoursStale: null,
  assignedLOs: []
};
const viewChips: {
  key: ViewKey;
  label: string;
}[] = [{
  key: 'all',
  label: 'All Loans'
}, {
  key: 'critical',
  label: 'Critical (>72h)'
}, {
  key: 'dial',
  label: 'Dial Ready'
}, {
  key: 'mine',
  label: 'My Previous Leads'
}, {
  key: 'highvalue',
  label: 'High Value (>$500k)'
}];
const sortOptions: {
  key: SortKey;
  label: string;
}[] = [{
  key: 'urgency',
  label: 'Urgency'
}, {
  key: 'stale',
  label: 'Most Stale'
}, {
  key: 'amount',
  label: 'Highest Amount'
}, {
  key: 'recent',
  label: 'Recently Added'
}, {
  key: 'stage',
  label: 'Time in Stage'
}];
const stageOptions = ['Application Taken', 'Docs Out', 'Docs Signed', 'Processing'];
const stateOptions = ['CA', 'TX', 'FL', 'NY', 'OH', 'WA'];
interface FilterBarProps {
  activeView: ViewKey;
  onViewChange: (v: ViewKey) => void;
  sortKey: SortKey;
  onSortChange: (k: SortKey) => void;
  advanced: AdvancedFilters;
  onAdvancedChange: (f: AdvancedFilters) => void;
  loOptions: string[];
}
export function FilterBar({
  activeView,
  onViewChange,
  sortKey,
  onSortChange,
  advanced,
  onAdvancedChange,
  loOptions
}: FilterBarProps) {
  const [sortOpen, setSortOpen] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const advRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) setSortOpen(false);
      if (advRef.current && !advRef.current.contains(e.target as Node)) setAdvOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const toggleArr = (arr: string[], v: string) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];
  const advCount = advanced.stages.length + advanced.states.length + advanced.assignedLOs.length + (advanced.minAmount != null ? 1 : 0) + (advanced.maxAmount != null ? 1 : 0) + (advanced.minHoursStale != null ? 1 : 0);
  return <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6" data-id="element-80">
      <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 w-full sm:w-auto" data-id="element-81">
        <div className="flex items-center gap-2 pr-4 border-r border-slate-800" data-id="element-82">
          <Filter className="w-4 h-4 text-slate-400" data-id="element-83" />
          <span className="text-sm font-medium text-slate-300" data-id="element-84">Views:</span>
        </div>
        {viewChips.map(chip => <button key={chip.key} onClick={() => onViewChange(chip.key)} className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition-all ${activeView === chip.key ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'bg-slate-800/50 text-slate-400 border border-slate-700/50 hover:bg-slate-800 hover:text-slate-200'}`} data-id="element-85">
            {chip.label}
          </button>)}
      </div>

      <div className="flex items-center gap-3 shrink-0" data-id="element-86">
        {/* Advanced Filters */}
        <div ref={advRef} className="relative" data-id="element-87">
          <button onClick={() => setAdvOpen(v => !v)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${advCount > 0 ? 'bg-orange-500/10 border-orange-500/30 text-orange-300' : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:bg-slate-800'}`} data-id="element-88">
            <SlidersHorizontal className="w-4 h-4" data-id="element-89" />
            Advanced Filters
            {advCount > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-500 text-white" data-id="element-90">
                {advCount}
              </span>}
          </button>
          {advOpen && <div className="absolute right-0 mt-2 w-80 z-30 rounded-xl border border-slate-700 bg-slate-900 shadow-2xl p-4 space-y-4" data-id="element-91">
              <div className="flex items-center justify-between" data-id="element-92">
                <h4 className="text-sm font-bold text-white" data-id="element-93">
                  Advanced Filters
                </h4>
                <button onClick={() => onAdvancedChange(emptyAdvancedFilters)} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-id="element-94">
                  <X className="w-3 h-3" data-id="element-95" /> Clear
                </button>
              </div>

              <div data-id="element-96">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-97">
                  Loan Stage
                </label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-id="element-98">
                  {stageOptions.map(s => <button key={s} onClick={() => onAdvancedChange({
                ...advanced,
                stages: toggleArr(advanced.stages, s)
              })} className={`px-2 py-1 rounded text-xs border ${advanced.stages.includes(s) ? 'bg-orange-500/20 border-orange-500/40 text-orange-300' : 'bg-slate-800 border-slate-700 text-slate-300'}`} data-id="element-99">
                      {s}
                    </button>)}
                </div>
              </div>

              <div data-id="element-100">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-101">
                  State
                </label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-id="element-102">
                  {stateOptions.map(s => <button key={s} onClick={() => onAdvancedChange({
                ...advanced,
                states: toggleArr(advanced.states, s)
              })} className={`px-2 py-1 rounded text-xs border ${advanced.states.includes(s) ? 'bg-orange-500/20 border-orange-500/40 text-orange-300' : 'bg-slate-800 border-slate-700 text-slate-300'}`} data-id="element-103">
                      {s}
                    </button>)}
                </div>
              </div>

              <div data-id="element-104">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-105">
                  Loan Amount
                </label>
                <div className="grid grid-cols-2 gap-2 mt-1.5" data-id="element-106">
                  <input type="number" placeholder="Min" value={advanced.minAmount ?? ''} onChange={e => onAdvancedChange({
                ...advanced,
                minAmount: e.target.value ? Number(e.target.value) : null
              })} className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500/50" data-id="element-107" />
                  <input type="number" placeholder="Max" value={advanced.maxAmount ?? ''} onChange={e => onAdvancedChange({
                ...advanced,
                maxAmount: e.target.value ? Number(e.target.value) : null
              })} className="bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500/50" data-id="element-108" />
                </div>
              </div>

              <div data-id="element-109">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-110">
                  Min Hours Stale
                </label>
                <input type="number" placeholder="e.g. 48" value={advanced.minHoursStale ?? ''} onChange={e => onAdvancedChange({
              ...advanced,
              minHoursStale: e.target.value ? Number(e.target.value) : null
            })} className="w-full mt-1.5 bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-orange-500/50" data-id="element-111" />
              </div>

              {loOptions.length > 0 && <div data-id="element-112">
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400" data-id="element-113">
                    Assigned LO
                  </label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-id="element-114">
                    {loOptions.map(lo => <button key={lo} onClick={() => onAdvancedChange({
                ...advanced,
                assignedLOs: toggleArr(advanced.assignedLOs, lo)
              })} className={`px-2 py-1 rounded text-xs border ${advanced.assignedLOs.includes(lo) ? 'bg-orange-500/20 border-orange-500/40 text-orange-300' : 'bg-slate-800 border-slate-700 text-slate-300'}`} data-id="element-115">
                        {lo}
                      </button>)}
                  </div>
                </div>}
            </div>}
        </div>

        {/* Sort */}
        <div ref={sortRef} className="relative" data-id="element-116">
          <button onClick={() => setSortOpen(v => !v)} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm font-medium text-slate-300 hover:bg-slate-800 transition-colors" data-id="element-117">
            Sort by: {sortOptions.find(o => o.key === sortKey)?.label}
            <ChevronDown className={`w-4 h-4 transition-transform ${sortOpen ? 'rotate-180' : ''}`} data-id="element-118" />
          </button>
          {sortOpen && <div className="absolute right-0 mt-2 w-52 z-30 rounded-xl border border-slate-700 bg-slate-900 shadow-2xl py-1.5" data-id="element-119">
              {sortOptions.map(opt => <button key={opt.key} onClick={() => {
            onSortChange(opt.key);
            setSortOpen(false);
          }} className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-200 hover:bg-slate-800" data-id="element-120">
                  {opt.label}
                  {sortKey === opt.key && <Check className="w-4 h-4 text-orange-400" data-id="element-121" />}
                </button>)}
            </div>}
        </div>
      </div>
    </div>;
}